/**
 * Parse uploaded JSON file into normalized data structure
 */

import type { NameEntry } from '../types'

/**
 * Parse JSON format
 * Supports two formats:
 * 1. Wrapped: { "names": [{ "original": "...", "ipa": "...", "candidates": [...] }] }
 * 2. Direct array: [{ "latin": "...", "ipa": "...", "candidates": [...] }]
 *
 * Candidates can be strings or objects with { text, score, initialRank }
 */
const parseJSON = (fileText: string): NameEntry[] => {
  const data = JSON.parse(fileText)

  // Support both wrapped format {"names": [...]} and direct array format [...]
  let namesArray: {
    id?: string
    original?: string
    latin?: string
    ipa: string
    candidates: (
      | string
      | {
          id?: string
          text: string
          score?: number
          normalizedScore?: number
          initialRank?: number
        }
    )[]
  }[]

  if (Array.isArray(data)) {
    namesArray = data
  } else if (data.names && Array.isArray(data.names)) {
    namesArray = data.names
  } else {
    throw new Error('JSON must contain either a "names" array or be a direct array of name objects')
  }

  return namesArray.map((nameObj, i: number) => {
    // Support both "original" and "latin" field names
    const original = nameObj.original || nameObj.latin

    if (!original || !nameObj.ipa) {
      throw new Error(`Name ${i + 1}: Missing "original"/"latin" or "ipa" field`)
    }

    if (!nameObj.candidates || !Array.isArray(nameObj.candidates)) {
      throw new Error(`Name ${i + 1}: Missing or invalid "candidates" array`)
    }

    if (nameObj.candidates.length !== 5) {
      throw new Error(`Name ${i + 1}: Expected exactly 5 candidates, got ${nameObj.candidates.length}`)
    }

    return {
      id: nameObj.id || `name_${i}`,
      original,
      ipa: nameObj.ipa,
      candidates: nameObj.candidates.map((candidate, candidateIndex: number) => {
        // Validate candidate is not null/undefined
        if (candidate === null || candidate === undefined) {
          throw new Error(`Name ${i + 1}, candidate ${candidateIndex + 1}: Candidate is null or undefined`)
        }

        // Support both string format and object format
        let candidateText: string
        if (typeof candidate === 'string') {
          candidateText = candidate
        } else if (typeof candidate === 'object' && candidate.text) {
          candidateText = candidate.text
        } else {
          throw new Error(`Name ${i + 1}, candidate ${candidateIndex + 1}: Missing or invalid "text" field`)
        }

        // Validate text is not empty
        if (!candidateText || candidateText.trim() === '') {
          throw new Error(`Name ${i + 1}, candidate ${candidateIndex + 1}: Candidate text cannot be empty`)
        }

        // Support score and normalizedScore fields
        let candidateScore: number | undefined
        let candidateNormalizedScore: number | undefined
        if (typeof candidate === 'object') {
          candidateScore = candidate.score
          candidateNormalizedScore = candidate.normalizedScore
        }

        // Use initialRank from data if present, otherwise use array position
        const originalRank =
          typeof candidate === 'object' && candidate.initialRank ? candidate.initialRank : candidateIndex + 1

        return {
          id: typeof candidate === 'object' && candidate.id ? candidate.id : `name_${i}_c${candidateIndex}`,
          text: candidateText,
          originalRank,
          currentRank: originalRank,
          ...(candidateScore !== undefined && { score: candidateScore }), // Include score if present
          ...(candidateNormalizedScore !== undefined && { normalizedScore: candidateNormalizedScore }), // Include normalizedScore if present
        }
      }),
      evaluated: false,
    }
  })
}

/**
 * Main entry point for file parsing
 */
export const parseFile = async (file: File): Promise<NameEntry[]> => {
  const fileText = await file.text()

  // Only support JSON format
  if (!file.name.toLowerCase().endsWith('.json')) {
    throw new Error('Only JSON files are supported')
  }

  return parseJSON(fileText)
}

/**
 * Validate file size
 */
export const validateFileSize = (file: File, maxSizeMB = 10): void => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024
  if (file.size > maxSizeBytes) {
    throw new Error(`File too large. Maximum size is ${maxSizeMB}MB`)
  }
}

/**
 * Validate file type
 */
export const validateFileType = (file: File): void => {
  if (!file.name.toLowerCase().endsWith('.json')) {
    throw new Error('Invalid file type. Only JSON files (.json) are supported')
  }
}
